// Models
import {
  IExerciseData,
  IExerciseListPayload,
  IExerciseState,
} from 'storage/exercise/models'
import {
  IFormInputs,
  IModelInput,
  ISetToExerciseInput,
  IWorkoutSetInput,
  TMuscleGroupFilter,
  TSpecialSetInput,
} from 'models'
import { IWorkoutRoutineState } from 'storage/workoutRoutine/models'
import { IWorkoutModelState } from 'storage/workoutModel/models'
import IStore from 'lib/redux/models'

// React
import { FC, useContext, useEffect, useLayoutEffect, useState } from 'react'

// Libraries
import { ThemeContext } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import isEqual from 'lodash.isequal'

// Misc
import { getDivisionType, getModelName, uid } from 'utils/functions'
import { prepareModelsToRedux } from 'filters/workouts'
import { success } from 'storage/workoutModel/duck'
import {
  triggerLoadExercises,
  triggerLoadMoreExercises,
} from 'storage/exercise/duck'
import { triggerLoadWorkoutRoutine } from 'storage/workoutRoutine/duck'
import { useDebounceFunction, useMediaQuery } from 'hooks'
import { workoutModelsSchema } from 'schemas'
import useFilters from 'hooks/useFilters'

// Components
import { Loading } from 'heeds-ds'
import Desktop from './desktop'
import Mobile from './mobile'

// Constants
const FILTERS = [
  { param: 'body_part', value: 'NEC', label: 'Trapézio' },
  { param: 'body_part', value: 'SHO', label: 'Ombro' },
  { param: 'body_part', value: 'CHE', label: 'Peitoral' },
  { param: 'body_part', value: 'BAC', label: 'Costas' },
  { param: 'body_part', value: 'UPA', label: 'Braço' },
  { param: 'body_part', value: 'LOA', label: 'Antebraço' },
  { param: 'body_part', value: 'WAI', label: 'Abdômen' },
  {
    param: 'body_part',
    value: 'LOA,UPA,CHE,BAC,WAI,NEC,SHO',
    label: 'Superior completo',
  },
  { param: 'body_part', value: 'ULE', label: 'Glúteos e Coxa' },
  { param: 'body_part', value: 'LLE', label: 'Panturrilha' },
  { param: 'body_part', value: 'LLE,ULE', label: 'Inferior completo' },
  { param: 'body_part', value: 'CAR', label: 'Aeróbico' },
]

const page_size = 10

interface IFiltersQuery extends IExerciseListPayload {
  etapa: '1' | '2' | '3'
  'treino-selecionado': string
}

const WorkoutModels: FC = () => {
  const { routine_id = '' } = useParams()
  const { exercises, loading: loadingExercise } = useSelector<
    IStore,
    IExerciseState
  >((state) => state.exercise)
  const { loading: loadingModel, workoutModels } = useSelector<
    IStore,
    IWorkoutModelState
  >((state) => state.workoutModel)
  const { loading: loadingRoutine, workoutRoutine } = useSelector<
    IStore,
    IWorkoutRoutineState
  >((state) => state.workoutRoutine)
  const { filters, isValueIncluded, setFilter, toggleFilter, resetFilters } =
    useFilters<IFiltersQuery>(window.location.search)
  const dispatch = useDispatch()
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.desktop}px)`)

  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedModelIndex, setSelectedModelIndex] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [specialSet, setSpecialSet] = useState<TSpecialSetInput>()

  const division = searchParams.get('divisao-da-rotina')
  const [stateModels, setStateModels] = useState<IModelInput[]>(
    workoutModels && workoutModels.length > 0
      ? workoutModels
      : [
          {
            id: 'NEW' + uid(),
            name: getModelName(0, division),
            workout_set: {},
            workout_routine: workoutRoutine?.id || 1,
          },
        ],
  )

  const methods = useForm<IFormInputs>({
    resolver: yupResolver(workoutModelsSchema),
    delayError: 800,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { models: stateModels },
  })
  const { control, setValue, watch } = methods
  const { append, fields, remove } = useFieldArray({
    control,
    name: `models`,
  })

  const formValues = watch()

  const modelSelect = filters['treino-selecionado']

  const onChangeFilters = (filtersA: TMuscleGroupFilter[]) => {
    const newFilters: IFiltersQuery = {
      ...filters,
      body_part: filtersA.flatMap((filter) =>
        filter.active ? filter.name : [],
      ),
    }

    resetFilters(newFilters)

    handleReloadExercises(newFilters)
  }

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filters = setFilter('name', event.target.value, true)
    debouncedLoad(filters)
  }

  const handleEndReached = () => {
    if (exercises && !loadingExercise && exercises?.next) {
      const query = {
        ...filters,
        page: exercises.next,
        page_size,
      }
      dispatch(triggerLoadMoreExercises(query))
    }
  }

  const handleReloadExercises = (newFilters: IExerciseListPayload) => {
    const query = {
      ...newFilters,
      page_size,
    }
    dispatch(triggerLoadExercises(query))
  }

  const debouncedLoad = useDebounceFunction<
    (newFilters: IExerciseListPayload) => void
  >(handleReloadExercises, 1000)

  const selectWorkoutModel = (modelIndex: number) => {
    setSelectedModelIndex(modelIndex)
  }

  const addWorkoutModel = (model: IModelInput) => {
    append(model)
    setStateModels([...stateModels, model])

    stateModels && setSelectedModelIndex(stateModels.length)
  }

  const duplicateWorkoutModel = (index: number) => {
    const { name, workout_set } = stateModels[index]
    const field = fields[index]
    const fieldCopy = { ...field, id: 'NEW' + uid(), name: `Cópia de ${name}` }

    append(fieldCopy)

    setStateModels([
      ...stateModels,
      { ...fieldCopy, workout_set: { ...workout_set } },
    ])

    stateModels && setSelectedModelIndex(stateModels.length)
  }

  const removeWorkoutModel = (id: string) => {
    let updateModels = stateModels.filter((model) => model.id !== id)

    remove(stateModels.findIndex((model) => model.id === id))

    if (updateModels.length === 0) {
      updateModels = [
        {
          id: 'NEW' + uid(),
          name: getModelName(0, getDivisionType(stateModels[0].name)),
          workout_set: {},
          workout_routine: workoutRoutine?.id || 1,
        },
      ]
      append(updateModels)
    }

    setStateModels(updateModels)
    setSelectedModelIndex(Math.max(updateModels.length - 1, 0))
  }

  const updateWorkoutModel = (modelId: string, updatedModel: IModelInput) => {
    const index = stateModels.findIndex((model) => model.id === modelId)
    const updatedModels = [...stateModels]
    updatedModels[index] = { ...updatedModel }

    setStateModels(updatedModels)
  }

  const copyWorkoutSetFields = (
    set: IWorkoutSetInput,
    setToCopy: IWorkoutSetInput,
  ) => {
    const { interval, quantity, exercises } = setToCopy

    if (setToCopy.id === set.id) return setToCopy

    return {
      ...set,
      interval,
      quantity,
      repeat: true,
      exercises: set.exercises.map((exercise) => ({
        ...exercise,
        repetitions: exercises[0].repetitions,
        cadence: exercises[0].cadence,
      })),
    }
  }

  const addWorkoutSet = (modelId: string, newSet: IWorkoutSetInput) => {
    const index = stateModels.findIndex((model) => model.id === modelId)
    const model = { ...stateModels[index] }

    const setsWithRepeatIsTrue = Object.values(
      formValues.models[index]?.workout_set || {},
    )?.filter((set) => set.repeat)

    const validSet = setsWithRepeatIsTrue?.length
      ? copyWorkoutSetFields(newSet, setsWithRepeatIsTrue[0])
      : newSet

    updateWorkoutModel(modelId, {
      ...model,
      workout_set: { ...model.workout_set, [newSet.id]: { ...validSet } },
    })
    setValue(`models.${index}.workout_set.${newSet.id}`, validSet)
  }

  const replicateWorkoutSetFields = (
    modelId: string,
    setToCopy: IWorkoutSetInput,
  ) => {
    const index = stateModels.findIndex((model) => model.id === modelId)
    const model = { ...stateModels[index] }

    const updatedWorkoutSets = Object.values(model.workout_set).reduce(
      (acc, set) => {
        return { ...acc, [set.id]: copyWorkoutSetFields(set, setToCopy) }
      },
      {},
    )

    updateWorkoutModel(modelId, {
      ...model,
      workout_set: updatedWorkoutSets,
    })
    setValue(`models.${index}.workout_set`, updatedWorkoutSets)
  }

  const removeWorkoutSet = (modelId: string, workoutSetId: string) => {
    const index = stateModels.findIndex((model) => model.id === modelId)
    const model = { ...stateModels[index] }
    const updatedWorkoutSet = { ...model.workout_set }
    delete updatedWorkoutSet[workoutSetId]

    updateWorkoutModel(modelId, {
      ...model,
      workout_set: updatedWorkoutSet,
    })
  }

  const updateWorkoutSet = (
    modelId: string,
    workoutSetId: string,
    updatedWorkoutSet: IWorkoutSetInput,
  ) => {
    const index = stateModels.findIndex((model) => model.id === modelId)
    const model = stateModels[index]
    const updatedWorkoutSets = Object.values(model.workout_set)
      .map((workoutSet) =>
        workoutSet.id === workoutSetId ? updatedWorkoutSet : workoutSet,
      )
      .reduce((acc, set) => ({ ...acc, [set.id]: set }), {})
    updateWorkoutModel(modelId, { ...model, workout_set: updatedWorkoutSets })
  }

  const addExerciseToWorkoutSet = (
    modelId: string,
    workoutSetId: string,
    exercise: IExerciseData,
  ) => {
    const newExercise = {
      id: uid(),
      cadence: '',
      weight: '',
      repetitions: '',
      exercise,
    }

    if (specialSet) {
      setSpecialSet({
        ...specialSet,
        interval: specialSet.interval,
        exercises: { ...specialSet.exercises, [newExercise.id]: newExercise },
      })
      return
    }

    const index = stateModels.findIndex((model) => model.id === modelId)
    const model = stateModels[index]
    const setIndex = Object.values(model.workout_set).findIndex(
      (model) => model.id === workoutSetId,
    )
    const workoutSet = model.workout_set[setIndex]
    updateWorkoutSet(modelId, workoutSet.id, {
      ...workoutSet,
      exercises: [...workoutSet.exercises, newExercise],
    })
  }

  const removeExerciseFromWorkoutSet = (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => {
    if (specialSet) {
      const specialExercises: Record<string, ISetToExerciseInput> =
        Object.values(specialSet.exercises).reduce((acc, stateSet, index) => {
          if (index !== exerciseIndex)
            return { ...acc, [stateSet.id]: stateSet }
          return acc
        }, {})
      setSpecialSet({
        ...specialSet,
        exercises: specialExercises,
      })
      return
    }

    const index = stateModels.findIndex((model) => model.id === modelId)
    const model = stateModels[index]
    const setIndex = Object.values(model.workout_set).findIndex(
      (model) => model.id === workoutSetId,
    )
    const workoutSet = model.workout_set[setIndex]
    updateWorkoutSet(modelId, workoutSetId, {
      ...workoutSet,
      exercises: workoutSet.exercises.filter(
        (_exercise, index) => index !== exerciseIndex,
      ),
    })
  }

  const onClickFilter = () => {
    setShowFilters((prev) => !prev)
  }

  const removeFilter = (label: string) => {
    const newFilters = toggleFilter('body_part', label, false)
    handleReloadExercises(newFilters)
  }

  const changePage = (page: string) => setSearchParams({ etapa: page })

  const workoutRoutineProps = {
    addExerciseToWorkoutSet,
    addWorkoutModel,
    addWorkoutSet,
    duplicateWorkoutModel,
    filters: FILTERS.map((filter) => ({
      active: isValueIncluded(filter.param, filter.value.toString()) || false,
      name: filter.value,
      label: filter.label,
    })),
    inputValue: filters?.name || '',
    loadMoreExercises: handleEndReached,
    methods,
    onChangeFilters,
    onClickFilter,
    onSearchChange,
    removeExerciseFromWorkoutSet,
    removeFilter,
    removeWorkoutModel,
    removeWorkoutSet,
    replicateWorkoutSetFields,
    selectWorkoutModel,
    selectedModelIndex,
    setSpecialSet,
    showFilters,
    specialSet,
    stateModels,
    updateWorkoutModel,
    updateWorkoutSet,
  }

  useEffect(() => {
    const payload = prepareModelsToRedux(formValues, stateModels)

    if (!isEqual(payload, workoutModels)) {
      setStateModels(payload)
      dispatch(success({ workoutModels: payload }))
    }
  }, [dispatch, formValues, stateModels, workoutModels])

  useEffect(() => {
    modelSelect && setSelectedModelIndex(parseInt(modelSelect))
  }, [modelSelect])

  useLayoutEffect(() => {
    if (!workoutRoutine) {
      dispatch(triggerLoadWorkoutRoutine({ routine_pk: parseInt(routine_id) }))
    }
    if (!exercises?.results && !loadingExercise) {
      dispatch(triggerLoadExercises({ ...filters, page_size }))
    }
  }, [
    dispatch,
    exercises?.results,
    filters,
    loadingExercise,
    routine_id,
    workoutRoutine,
  ])

  if (loadingModel || loadingRoutine) {
    return <Loading active />
  }

  return isDesktop ? (
    <Desktop {...workoutRoutineProps} />
  ) : (
    <Mobile
      {...workoutRoutineProps}
      changePage={changePage}
      mobilePage={filters.etapa}
    />
  )
}

export default WorkoutModels
